/**
 * Generates an SVG path for a polygon with rounded corners.
 * Uses quadratic Bezier curves (Q command) to create the corners.
 * @param {Array<Array<number>>} points - Array of [x, y] coordinates for the vertices
 * @param {number} radius - The radius of the rounded corners
 * @returns {string} The SVG path data string
 */
export const getRoundedPath = (points, radius) => {
  const d = points.map((p, i) => {
    const prev = points[(i - 1 + points.length) % points.length];
    const next = points[(i + 1) % points.length];
    
    const vp = { x: prev[0] - p[0], y: prev[1] - p[1] };
    const vn = { x: next[0] - p[0], y: next[1] - p[1] };
    
    const lp = Math.sqrt(vp.x * vp.x + vp.y * vp.y);
    const ln = Math.sqrt(vn.x * vn.x + vn.y * vn.y);
    
    const up = { x: vp.x / lp, y: vp.y / lp };
    const un = { x: vn.x / ln, y: vn.y / ln };
    
    // Clamp radius to half the shortest connected side to avoid artifacts
    const r = Math.min(radius, lp / 2, ln / 2);
    
    const startX = p[0] + up.x * r;
    const startY = p[1] + up.y * r;
    
    const endX = p[0] + un.x * r;
    const endY = p[1] + un.y * r;
    
    return { start: [startX, startY], end: [endX, endY], control: p };
  });

  // Start at the end of the curve of the first point (which is on the segment to the second point)
  let path = `M ${d[0].end[0]} ${d[0].end[1]} `;
  
  for (let i = 1; i < points.length; i++) {
    // Line to start of next corner
    path += `L ${d[i].start[0]} ${d[i].start[1]} `;
    // Curve to end of next corner
    path += `Q ${d[i].control[0]} ${d[i].control[1]} ${d[i].end[0]} ${d[i].end[1]} `;
  }
  
  // Close back to first point
  path += `L ${d[0].start[0]} ${d[0].start[1]} `;
  path += `Q ${d[0].control[0]} ${d[0].control[1]} ${d[0].end[0]} ${d[0].end[1]} `;
  
  return path;
};
